import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import mabarService from '@/services/mabarService';
import { getClientProfile, updateClientProfile, DataProfile } from '@/services/clientProfile';
import SEO from '@/components/common/SEO';
import {
  INVASION_METRIC_CATALOG,
  RACKET_METRIC_CATALOG,
  SportCareerStat,
} from '@/components/client/mobile/RaporEventCard';

export default function ClientMabarEventKelolaStatistikPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<DataProfile | null>(null);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [isRacket, setIsRacket] = useState(false);
  const [sportKey, setSportKey] = useState('futsal');

  const [eventStats, setEventStats] = useState<SportCareerStat>({
    goals: 0,
    assists: 0,
    totalGames: 0,
    wins: 0,
    point: 0,
    pointLoss: 0,
    motm: 0,
    cleansheets: 0,
    keyPasses: 0,
    saves: 0,
    clearances: 0,
    fouls: 0,
    freestyle: 0,
    winstreak: 0,
  });

  useEffect(() => {
    if (!slug) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const [profRes, eventRes, statsRes] = await Promise.all([
          getClientProfile().catch(() => null),
          mabarService.getEventBySlug(slug).catch(() => null),
          mabarService.getEventMyStats(slug).catch(() => null),
        ]);

        const profData = profRes?.success ? profRes.data : null;
        const evDetail = eventRes;

        if (profData) setProfile(profData);

        const sportCategoryName = (
          evDetail?.sport_category?.slug ||
          evDetail?.sport_category?.name ||
          ''
        ).toLowerCase();

        const racketCheck = ['padel', 'badminton'].some((s) => sportCategoryName.includes(s));
        setIsRacket(racketCheck);

        const sKey = racketCheck
          ? (sportCategoryName.includes('badminton') ? 'badminton' : 'padel')
          : (sportCategoryName.includes('minisoccer') ? 'minisoccer' : sportCategoryName.includes('sepak') ? 'sepakbola' : 'futsal');

        setSportKey(sKey);

        if (statsRes) {
          const st = statsRes;
          setEventStats({
            goals: st.goals || 0,
            assists: st.assists || 0,
            wins: Math.round(((st.win_rate || 0) * (st.matches || 0)) / 100),
            totalGames: st.matches || 0,
            point: st.point || 0,
            pointLoss: st.point_loss || 0,
            isRacket: st.is_racket || false,
            cleansheets: (st as any).clean_sheet ?? (st as any).cleansheet ?? 0,
            keyPasses: (st as any).key_passes ?? (st as any).key_pass ?? 0,
            saves: (st as any).saves ?? (st as any).save ?? 0,
            clearances: (st as any).clearances ?? (st as any).clearance ?? 0,
            fouls: (st as any).fouls ?? (st as any).foul ?? 0,
            motm: (st as any).motm ?? 0,
            freestyle: (st as any).freestyle ?? 0,
            winstreak: (st as any).winstreak ?? 0,
          });
        }

        // Load saved export stat config / stat config
        const savedConfig =
          profData?.information?.export_stat_config?.[sKey] ||
          profData?.information?.stat_config?.[sKey];
        const localSaved = JSON.parse(
          localStorage.getItem(`sporto_export_stat_config_${sKey}`) || 'null'
        );

        const defaultInvasionKeys =
          sKey === 'minisoccer'
            ? ['gol', 'assist', 'cleansheet', 'freestyle']
            : ['gol', 'assist', 'key_pass', 'cleansheet'];

        const initialKeys =
          (Array.isArray(savedConfig) && savedConfig.length === 4 && savedConfig) ||
          (Array.isArray(localSaved) && localSaved.length === 4 && localSaved) ||
          (racketCheck ? ['poin', 'point_loss', 'total_match', 'winrate'] : defaultInvasionKeys);

        setSelectedKeys(initialKeys);
      } catch (err) {
        console.error('Error loading kelola statistik page:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [slug]);

  const activeCatalog = isRacket ? RACKET_METRIC_CATALOG : INVASION_METRIC_CATALOG;
  const calculatedWinRate =
    eventStats.totalGames > 0 ? Math.round((eventStats.wins / eventStats.totalGames) * 100) : 0;

  const handleToggleMetric = (key: string) => {
    const isSelected = selectedKeys.includes(key);
    if (isSelected) {
      setSelectedKeys((prev) => prev.filter((k) => k !== key));
    } else {
      if (selectedKeys.length >= 4) {
        showToast.error('Maksimal pilih 4 metrik statistik');
        return;
      }
      setSelectedKeys((prev) => [...prev, key]);
    }
  };

  const handleSave = async () => {
    if (selectedKeys.length !== 4) {
      showToast.error('Pilih tepat 4 metrik statistik');
      return;
    }

    try {
      setSaving(true);

      const updatedExportConfig = {
        ...(profile?.information?.export_stat_config || {}),
        [sportKey]: selectedKeys,
      };

      const updatedStatConfig = {
        ...(profile?.information?.stat_config || {}),
        [sportKey]: selectedKeys,
      };

      localStorage.setItem(
        `sporto_export_stat_config_${sportKey}`,
        JSON.stringify(selectedKeys)
      );

      await updateClientProfile({
        information: {
          ...(profile?.information || {}),
          export_stat_config: updatedExportConfig,
          stat_config: updatedStatConfig,
        },
      });

      showToast.success('Pengaturan statistik berhasil disimpan!');
      navigate(-1);
    } catch (err: any) {
      console.error('Failed to save export stat config:', err);
      showToast.error(err?.message || 'Gagal menyimpan pengaturan statistik');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-gray-900 flex flex-col justify-between select-none">
      <SEO title="Kelola Statistik Rapor" description="Pilih metrik statistik laporan" />

      {/* Subtitle / Counter Banner */}
      <div className="bg-white border-b border-gray-100 px-4 py-3 max-w-2xl mx-auto w-full flex items-center justify-between shrink-0 shadow-2xs">
        <span className="text-xs text-gray-500 font-medium">
          Pilih tepat 4 metrik statistik
        </span>
        <span
          className={`text-xs font-bold px-2.5 py-1 rounded-full ${
            selectedKeys.length === 4 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
          }`}
        >
          {selectedKeys.length}/4 terpilih
        </span>
      </div>

      {/* Scrollable Metric List */}
      <div className="flex-1 p-4 pb-28 flex flex-col gap-2 max-w-2xl mx-auto w-full">
        {loading ? (
          <div className="flex flex-col gap-3 py-6 animate-pulse">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-white rounded-2xl border border-gray-100" />
            ))}
          </div>
        ) : (
          activeCatalog.map((metric) => {
            const isSelected = selectedKeys.includes(metric.key);
            const formattedVal = metric.getValue(eventStats, calculatedWinRate);

            return (
              <div
                key={metric.key}
                onClick={() => handleToggleMetric(metric.key)}
                className={`flex items-center justify-between p-3.5 rounded-2xl transition-all cursor-pointer select-none ${
                  isSelected
                    ? 'bg-white border border-gray-200 shadow-xs'
                    : 'bg-transparent border border-transparent hover:bg-gray-100/50'
                }`}
              >
                <div className="flex items-center gap-3.5 min-w-0">
                  {/* Checkbox */}
                  <div
                    className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-all ${
                      isSelected
                        ? 'bg-[#FC480D] text-white border border-[#FC480D]'
                        : 'border-2 border-gray-300 bg-white'
                    }`}
                  >
                    {isSelected && <Icon icon="ph:check-bold" className="w-3.5 h-3.5" />}
                  </div>

                  {/* Metric Icon */}
                  <div
                    className={`w-9 h-9 rounded-xl ${metric.iconBg} ${metric.iconColor} flex items-center justify-center shrink-0 ${
                      !isSelected ? 'opacity-50 grayscale-[30%]' : ''
                    }`}
                  >
                    <Icon icon={metric.icon} className="w-4.5 h-4.5" />
                  </div>

                  {/* Metric Label */}
                  <span
                    className={`text-sm font-semibold truncate ${
                      isSelected ? 'text-gray-900' : 'text-gray-400'
                    }`}
                  >
                    {metric.label}
                  </span>
                </div>

                {/* Metric Value */}
                <span
                  className={`text-base shrink-0 ml-2 ${
                    isSelected ? 'font-bold text-gray-900' : 'font-normal text-gray-300'
                  }`}
                >
                  {formattedVal}
                </span>
              </div>
            );
          })
        )}
      </div>

      {/* FIXED BOTTOM ACTION BAR */}
      <div className="fixed bottom-0 left-0 right-0 max-w-2xl mx-auto bg-white/95 backdrop-blur-md border-t border-gray-100 p-3.5 px-4 z-40 flex items-center shadow-lg">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || selectedKeys.length !== 4}
          className="flex-1 h-12 font-bold text-sm rounded-full transition-all active:scale-98 flex items-center justify-center gap-2 cursor-pointer bg-[#FC480D] hover:bg-[#D83B08] text-white shadow-md disabled:opacity-40"
        >
          {saving ? (
            <>
              <Icon icon="ph:spinner-bold" className="w-5 h-5 animate-spin" />
              <span>Menyimpan...</span>
            </>
          ) : (
            <span>Simpan perubahan</span>
          )}
        </button>
      </div>
    </div>
  );
}
